import { map } from 'lodash';
import { batch } from 'react-redux';
import { push } from 'redux-first-history';
import {
  getSessionUser, getFingerprint, getCurrentPosition, getUserId,
} from '@client/selectors/helpers/session';
import {
  getCurrentSKU, getCurrentSKUAmount, getIdempotencyKey, getShowTotalCalculator,
} from '@client/selectors/features/stripe';
import { getCurrentShow, getCurrentShowId } from '@client/selectors/entities/shows';
import {
  startCheckoutSession, startCreateStripeUnifiedSale, startCheckoutFreeTickets,
  updateStripeIdempotencyKey, startReserveTickets, setCurrentOrder,
} from '@client/slices/features/stripe.reducer';
import { setLoaderIsFinishedError, setLoaderIsFinishedSuccess, setLoaderIsLoading } from '@client/slices/helpers/loaders.reducer';
import { trackAnalytics } from '@client/intercom';
import { showError } from '@client/listeners/lib/alerts';
import { firebaseRequest, handleRequest } from '@client/listeners/lib/requests';
import { setTicket, startRedeemShowTicket, upsertTickets } from '@client/slices/entities/tickets.reducer';
import { baseUrl, getQueryVariable, getShowPath } from '@sda/utils/url';
import { enrichUser } from '@client/listeners/utils/session';
import { getDisplayName, getNameParts } from '@sda/utils/string';
import { startAffiliateLinkImpression } from '@client/slices/entities/affiliates.reducer';
import { showSnackbar } from '@client/listeners/lib/snackbar';
import { AnalyticsEvents } from '@sda/interfaces/queue/analytics';
import { OrderTypes } from '@sda/enums';
import { startAddNameToUser, updateCurrentSessionUser } from '@client/slices/helpers/session.reducer';
import type { AddListener } from '@client/listeners/lib';
import type { RootState, AppDispatch } from '@client/store';
import type { ListenerEffectAPI } from '@reduxjs/toolkit';
import type { CreateTicketCheckoutSessionDto } from '@sda/functions/stripe/post-checkout.endpoint';
import type { ReserveTicketsDto } from '@sda/functions/stripe/post-reserve.endpoint';
import type { PaymentTokenDto, PaymentTokenResponse } from '@sda/functions/stripe/post-apply-payment-intent.endpoint';
import type { CreateUnifiedOrderDto } from '@sda/functions/stripe/post-stripe-order.endpoint';
import type { SKUPriceList } from '@sda/interfaces/reducer/stripe.reducer';
import type { PrivateTicket, Ticket } from '@sda/interfaces/ticket';
import type { FetchDataType } from '@sda/sdk/interfaces';

const reserveShowTickets = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>) => {
  const state = listenerApi.getState();

  const showId = getCurrentShowId(state);

  const fingerprint = getFingerprint(state);
  const currentSKUAmount = getCurrentSKUAmount(state);

  const request = await firebaseRequest<ReserveTicketsDto>({
    url: `${process.env.API_BASE_URL}/stripe/reserve`,
    method: 'POST',
    body: {
      showId,
      fingerprint,
      quantity: currentSKUAmount,
    },
  });

  const response = await handleRequest(request);

  return response;
};

export const showsListeners = (addListener: AddListener) => [
  addListener({
    actionCreator: startCheckoutFreeTickets,
    effect: async (action, listenerApi) => {
      const { shareContact, fullName, email } = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const showId = getCurrentShowId(state);

      const scope = 'start-checkout-session';
      dispatch(setLoaderIsLoading(scope));

      try {
        const response = await reserveShowTickets(listenerApi);

        if (response.status === true) {
          dispatch(startCreateStripeUnifiedSale({
            email,
            shareContact,
            showId,
            name: fullName,
          }));

          trackAnalytics(startCheckoutFreeTickets, { showId });
        } else {
          showError(response.error);
          dispatch(setLoaderIsFinishedError(scope, response.error));
        }
      } catch (e) {
        showError(e);
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),

  addListener({
    actionCreator: startCheckoutSession,
    effect: async (action, listenerApi) => {
      const { shareContact, successUrl, scope = 'start-checkout-session' } = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const showId = getCurrentShowId(state);

      const fingerprint = getFingerprint(state);
      const currentSKUAmount = getCurrentSKUAmount(state);
      dispatch(setLoaderIsLoading(scope));

      try {
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(process.env.STRIPE_PUBLISHABLE_KEY);

        const response = await reserveShowTickets(listenerApi);

        if (response.status === true) {
          trackAnalytics(startReserveTickets, { showId });
          const currentSKU = getCurrentSKU(state);

          const { price } = currentSKU;

          const orderItems = map(price as SKUPriceList, (productPrice, productId) => ({
            price: productPrice,
            skuId: productId,
            quantity: currentSKUAmount[productId],
          }));

          const order = {
            currency: currentSKU.currency,
            items: orderItems,
          };

          const stripeIdempotencyKey = getIdempotencyKey(state);
          const position = getCurrentPosition(state);
          const utmSource = getQueryVariable('utm_source') || null;

          const checkoutRequest = await firebaseRequest<CreateTicketCheckoutSessionDto>({
            url: `${process.env.API_BASE_URL}/stripe/checkout`,
            method: 'POST',
            body: {
              order,
              stripeIdempotencyKey,
              fingerprint,
              showId,
              position,
              utmSource,
              shareContact,
              successUrl: successUrl || baseUrl('/checkout/success'),
            },
          });

          const checkoutResponse = await handleRequest<string>(checkoutRequest);

          if (checkoutResponse.status === false) {
            throw new Error(checkoutResponse.error.message);
          }

          trackAnalytics(startCheckoutSession, { showId });

          const result = await stripe.redirectToCheckout({
            sessionId: checkoutResponse.data,
          });

          if (result.error) {
            throw new Error(result.error.message);
          }

          // Don't trigger success for this one - we just want to leave it loading until the redirect occurs
        } else {
          showError(response.error);
          dispatch(setLoaderIsFinishedError(scope, response.error));
        }
      } catch (e) {
        showError(e);
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),

  addListener({
    actionCreator: startReserveTickets,
    effect: async (action, listenerApi) => {
      const { shareContact, scope = 'reserve-show-tickets' } = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const showId = getCurrentShowId(state);
      dispatch(setLoaderIsLoading(scope));

      try {
        const response = await reserveShowTickets(listenerApi);

        if (response.status === true) {
          trackAnalytics(startReserveTickets, { showId });

          dispatch(setLoaderIsFinishedSuccess(scope, { shareContact, checkout: true }));
        } else {
          showError(response.error);
          dispatch(setLoaderIsFinishedError(scope, response.error));
        }
      } catch (e) {
        showError(e);
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),

  addListener({
    actionCreator: startCreateStripeUnifiedSale,
    effect: async (action, listenerApi) => {
      let {
        token, email, name, showId,
        acceptsGDPR, shareContact,
      } = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      listenerApi.unsubscribe();
      listenerApi.cancelActiveListeners();

      const scope = 'start-checkout-session';
      dispatch(setLoaderIsLoading(scope));

      const user = getSessionUser(state);

      if (!email) {
        email = user.email;
      }

      if (!name) {
        name = getDisplayName(user);
      }

      const currentSKU = getCurrentSKU(state);
      const currentSKUAmount = getCurrentSKUAmount(state);

      const { price } = currentSKU;

      const orderItems = map(price as SKUPriceList, (productPrice, productId) => ({
        price: productPrice,
        skuId: productId,
        quantity: currentSKUAmount[productId],
      }));

      const order = {
        currency: currentSKU.currency,
        items: orderItems,
      };

      try {
        const stripeIdempotencyKey = getIdempotencyKey(state);
        const fingerprint = getFingerprint(state);
        const show = getCurrentShow(state);

        const position = getCurrentPosition(state);

        const utmSource = getQueryVariable('utm_source') || null;

        const orderRequest = await firebaseRequest<CreateUnifiedOrderDto>({
          url: `${process.env.API_BASE_URL}/stripe/orders`,
          method: 'POST',
          body: {
            order,
            stripeIdempotencyKey,
            fingerprint,
            showId,
            position,
            utmSource,
          },
        });

        const orderResponse = await handleRequest<string>(orderRequest);

        if (orderResponse.status === true) {
          const orderId = orderResponse.data;

          const paymentRequest = await firebaseRequest<PaymentTokenDto>({
            url: `${process.env.API_BASE_URL}/stripe/orders/${orderId}/applyPaymentIntent`,
            method: 'PUT',
            body: {
              orderId,
              token,
              name,
              email,
              showId,
              stripeIdempotencyKey,
              acceptsGDPR,
              shareContact,
              order,
              fingerprint,
              position,
              utmSource,
              price: 0,
            },
          });

          const response = await handleRequest<PaymentTokenResponse>(paymentRequest);

          if (response.status === false) {
            batch(() => {
              dispatch(setLoaderIsFinishedError(scope, response.error));
              dispatch(updateStripeIdempotencyKey());
            });

            showError(response.error.message);
          } else {
            const { ticketList, orderType, ...paymentData } = response.data;

            batch(() => {
              dispatch(setCurrentOrder(paymentData));
              dispatch(setLoaderIsFinishedSuccess(scope, { checkout: false }));
              dispatch(updateStripeIdempotencyKey());
              dispatch(upsertTickets(ticketList));
            });

            const total = getShowTotalCalculator(state, show);

            trackAnalytics(AnalyticsEvents.PURCHASED_TICKET, {
              showId,
              isAdvanceOrder: orderType === OrderTypes.ORDER_TYPE_ADVANCE,
              quantity: total.totalQuantity(),
              currency: total.currency(),
              artistTotal: total.artistTotal(),
              hostTotal: total.hostTotal(),
              sidedoorTotal: total.sidedoorTotal(),
              grossTotal: total.grossTotal(),
            });

            listenerApi.fork(async () => {
              await enrichUser(listenerApi);
            });
          }
        } else {
          throw new Error(orderResponse.error.message);
        }
      } catch (e) {
        batch(() => {
          dispatch(setLoaderIsFinishedError(scope, e));
          dispatch(updateStripeIdempotencyKey());
        });

        showError(e.message);
      } finally {
        listenerApi.subscribe();
      }
    },
  }),

  addListener({
    actionCreator: startAffiliateLinkImpression,
    effect: async (action, listenerApi) => {
      const { showId, affiliateLinkSource } = action.payload;
      const state = listenerApi.getState();

      const userId = getUserId(state);

      try {
        const request = await firebaseRequest({
          url: `${process.env.API_BASE_URL}/show/${showId}/affiliateImpression`,
          method: 'PUT',
          body: { source: affiliateLinkSource },
        });

        listenerApi.fork(async () => {
          await handleRequest(request);
        });

        trackAnalytics(startAffiliateLinkImpression, {
          showId,
          isSignedIn: !!userId,
          source: affiliateLinkSource,
        });
      } catch (e) {
        console.log(e);
      }
    },
  }),

  addListener({
    actionCreator: startRedeemShowTicket,
    effect: async (action, listenerApi) => {
      const { ticketId, values } = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const scope = `redeem-ticket-${ticketId}`;
      dispatch(setLoaderIsLoading(scope));

      const firebase = window.Firebase;
      const user = getSessionUser(state);

      try {
        const request = await firebaseRequest({
          url: `${process.env.API_BASE_URL}/ticket/${ticketId}/redeem`,
          method: 'PUT',
          body: values,
        });

        const response = await handleRequest<Pick<PrivateTicket, 'showId' | 'compiledAddress'>>(request);

        if (response.status === true) {
          const ticket = await firebase.SDK.Tickets.get(ticketId);

          const ticketInfo = {
            ...ticket,
            compiledAddress: response.data.compiledAddress,
            owner: user.id,
            guestEmail: user.email,
            guestName: `${user.firstName} ${user.lastName}`,
            pendingRedemption: false,
          };

          batch(() => {
            dispatch(setTicket(ticketInfo as Ticket & FetchDataType));
            dispatch(push(getShowPath(response.data.showId)));
            dispatch(setLoaderIsFinishedSuccess(scope));
          });

          trackAnalytics(startRedeemShowTicket, ticketInfo);
        } else {
          throw new Error(response.error.message);
        }
      } catch (e) {
        console.error(e);
        dispatch(setLoaderIsFinishedError(scope, e));
        showSnackbar(e.message, 'error');
      }
    },
  }),

  addListener({
    actionCreator: startAddNameToUser,
    effect: async (action, listenerApi) => {
      const { fullName } = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;
      const firebase = window.Firebase;

      const scope = 'add-name-form';
      dispatch(setLoaderIsLoading(scope));

      try {
        const { firstName, lastName } = getNameParts(fullName);
        const userId = getUserId(state);

        await firebase.SDK.Users.propagate(userId, { firstName, lastName, fullName });

        batch(() => {
          dispatch(updateCurrentSessionUser({ firstName, lastName, fullName }));
          dispatch(setLoaderIsFinishedSuccess(scope));
        });
      } catch (e) {
        showSnackbar(e, 'error');
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),

];
